import React, { useState, useEffect } from 'react';
import { getRoute, getMultiStopRoute, geocodeAddress, calculateBounds, getMarkerIcon, isMapConfigured } from '../services/mapService';
import { calculateETA, formatCoordinates } from '../services/gpsTrackingService';
import { recalcLoadETAs } from '../services/liveEtaService';
import { fmtWindowDateTime, fmtWindowTime } from '../utils/dbTime';   // __TZ_WINDOWS__

function RouteMapModal({ load, onClose }) {
  const [loading, setLoading] = useState(true);
  const [route, setRoute] = useState(null);
  const [stops, setStops] = useState([]);
  const [error, setError] = useState(null);
  const [selectedStop, setSelectedStop] = useState(null);
  const [liveEtas, setLiveEtas] = useState({});
  const [liveEtaLoading, setLiveEtaLoading] = useState(false);
  const [liveEtaMsg, setLiveEtaMsg] = useState(null);

  useEffect(() => {
    const fetchRouteData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get stops from load
        const loadStops = load.load_stops || [];
        const pickupStops = loadStops.filter(s => s.stop_type === 'pickup').sort((a, b) => a.stop_sequence - b.stop_sequence);
        const deliveryStops = loadStops.filter(s => s.stop_type === 'delivery').sort((a, b) => a.stop_sequence - b.stop_sequence);
        
        // Geocode all addresses
        const allStops = [...pickupStops, ...deliveryStops];
        const geocodedStops = await Promise.all(
          allStops.map(async (stop) => {
            const coords = await geocodeAddress(stop.address);
            return {
              ...stop,
              ...coords,
            };
          })
        );

        setStops(geocodedStops);

        // Calculate route if we have at least 2 stops
        if (geocodedStops.length >= 2) {
          const routeData = await getMultiStopRoute(geocodedStops);
          setRoute(routeData);
        }
      } catch (err) {
        console.error('Error fetching route data:', err);
        setError('Failed to calculate route');
      } finally {
        setLoading(false);
      }
    };

    fetchRouteData();
  }, [load]);

  const handleLiveEtas = async () => {
    setLiveEtaLoading(true);
    setLiveEtaMsg(null);
    try {
      const res = await recalcLoadETAs({ load: { ...load, load_stops: stops } });
      if (res.success) {
        const map = {};
        res.stops.forEach((s) => { map[s.id] = s; });
        setLiveEtas(map);
        setLiveEtaMsg(`Live from GPS${res.gps && res.gps.ageMinutes != null ? ` (${res.gps.ageMinutes}m old)` : ''}`);
      } else {
        setLiveEtaMsg(
          res.reason === 'no_gps' ? 'No GPS ping from this truck yet' :
          res.reason === 'no_remaining' ? 'All stops complete' :
          res.reason === 'no_coords' ? 'Stops missing coordinates' :
          'Live ETA unavailable'
        );
      }
    } catch (e) {
      setLiveEtaMsg('Live ETA unavailable');
    } finally {
      setLiveEtaLoading(false);
    }
  };

  const getStopColor = (stopType, index) => {
    if (stopType === 'pickup') {
      return 'var(--color-warning)'; // Orange for pickups
    }
    return 'var(--color-success)'; // Green for deliveries
  };

  const renderSimulatedMap = () => {
    if (stops.length === 0) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'var(--text-muted)'
        }}>
          No stops to display
        </div>
      );
    }

    const bounds = calculateBounds(stops);
    
    return (
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: 'var(--bg-primary)',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Simulated map background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, var(--bg-primary) 0%, var(--surface-1) 50%, var(--surface-2) 100%)',
          opacity: 0.8
        }} />

        {/* Grid lines for map effect */}
        <svg style={{ position: 'absolute', width: '100%', height: '100%', opacity: 0.1 }}>
          {[...Array(20)].map((_, i) => (
            <React.Fragment key={i}>
              <line x1={`${i * 5}%`} y1="0" x2={`${i * 5}%`} y2="100%" stroke="var(--accent-primary)" strokeWidth="1" />
              <line x1="0" y1={`${i * 5}%`} x2="100%" y2={`${i * 5}%`} stroke="var(--accent-primary)" strokeWidth="1" />
            </React.Fragment>
          ))}
        </svg>

        {/* Route line */}
        {route && route.waypoints && (
          <svg style={{ position: 'absolute', width: '100%', height: '100%' }}>
            <polyline
              points={route.waypoints.map((wp, i) => {
                const x = ((wp.lng - bounds.southwest.lng) / (bounds.northeast.lng - bounds.southwest.lng)) * 100;
                const y = 100 - ((wp.lat - bounds.southwest.lat) / (bounds.northeast.lat - bounds.southwest.lat)) * 100;
                return `${x}%,${y}%`;
              }).join(' ')}
              fill="none"
              stroke="var(--accent-primary)"
              strokeWidth="3"
              strokeDasharray="10,5"
              style={{ filter: 'drop-shadow(0 0 5px var(--accent-primary))' }}
            />
          </svg>
        )}

        {/* Stop markers */}
        {stops.map((stop, index) => {
          const x = ((stop.lng - bounds.southwest.lng) / (bounds.northeast.lng - bounds.southwest.lng)) * 100;
          const y = 100 - ((stop.lat - bounds.southwest.lat) / (bounds.northeast.lat - bounds.southwest.lat)) * 100;
          const color = getStopColor(stop.stop_type, index);
          const isSelected = selectedStop === index;

          return (
            <div
              key={stop.id || index}
              onClick={() => setSelectedStop(isSelected ? null : index)}
              style={{
                position: 'absolute',
                left: `calc(${x}% - 15px)`,
                top: `calc(${y}% - 15px)`,
                width: '30px',
                height: '30px',
                backgroundColor: color,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                border: isSelected ? '3px solid var(--text-primary)' : '2px solid rgba(255,255,255,0.5)',
                boxShadow: isSelected ? '0 0 15px rgba(255,255,255,0.5)' : '0 2px 10px rgba(0,0,0,0.3)',
                transition: 'all 0.2s ease',
                zIndex: isSelected ? 10 : 1
              }}
              title={stop.address}
            >
              {index + 1}
            </div>
          );
        })}

        {/* Selected stop info popup */}
        {selectedStop !== null && stops[selectedStop] && (
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            right: '20px',
            backgroundColor: 'rgba(33, 38, 45, 0.95)',
            borderRadius: '8px',
            padding: '1rem',
            border: '1px solid var(--accent-primary)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ 
                  color: getStopColor(stops[selectedStop].stop_type), 
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                  textTransform: 'uppercase',
                  fontSize: '0.8rem'
                }}>
                  {stops[selectedStop].stop_type} #{stops[selectedStop].stop_sequence}
                </div>
                <div style={{ color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                  {stops[selectedStop].address}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  {formatCoordinates(stops[selectedStop].lat, stops[selectedStop].lng)}
                </div>
                {stops[selectedStop].time_window_start && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    Time: {fmtWindowDateTime(stops[selectedStop].time_window_start)}
                  </div>
                )}
              </div>
              <button
                onClick={() => setSelectedStop(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  fontSize: '1.2rem'
                }}
              >
                x
              </button>
            </div>
          </div>
        )}

        {/* Map not configured notice */}
        {!isMapConfigured() && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: 'rgba(210, 153, 34, 0.9)',
            color: 'var(--text-primary)',
            padding: '0.5rem 1rem',
            borderRadius: '5px',
            fontSize: '0.8rem'
          }}>
            Simulation Mode - Configure Mapbox/Google Maps in Settings
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--surface-1)',
        borderRadius: '10px',
        padding: '1.5rem',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--surface-2)'
        }}>
          <div>
            <h2 style={{ color: 'var(--accent-primary)', margin: '0 0 0.25rem 0' }}>
              Route Map - Load #{load.load_number}
            </h2>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              {stops.length} stops | {route ? route.totalDistance?.text : 'Calculating...'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            x
          </button>
        </div>

        {/* Map Area */}
        <div style={{
          flex: 1,
          minHeight: '400px',
          marginBottom: '1rem'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--accent-primary)'
            }}>
              Loading route...
            </div>
          ) : error ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--color-danger)'
            }}>
              {error}
            </div>
          ) : (
            renderSimulatedMap()
          )}
        </div>

        {/* Route Summary */}
        {route && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <div style={{
              backgroundColor: 'var(--surface-2)',
              padding: '1rem',
              borderRadius: '5px',
              textAlign: 'center'
            }}>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                Total Distance
              </div>
              <div style={{ color: 'var(--accent-primary)', fontSize: '1.2rem', fontWeight: 'bold' }}>
                {route.totalDistance?.text || 'N/A'}
              </div>
            </div>
            <div style={{
              backgroundColor: 'var(--surface-2)',
              padding: '1rem',
              borderRadius: '5px',
              textAlign: 'center'
            }}>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                Estimated Time
              </div>
              <div style={{ color: 'var(--color-warning)', fontSize: '1.2rem', fontWeight: 'bold' }}>
                {route.totalDuration?.text || 'N/A'}
              </div>
            </div>
            <div style={{
              backgroundColor: 'var(--surface-2)',
              padding: '1rem',
              borderRadius: '5px',
              textAlign: 'center'
            }}>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                Stops
              </div>
              <div style={{ color: 'var(--accent-blue)', fontSize: '1.2rem', fontWeight: 'bold' }}>
                {stops.length}
              </div>
            </div>
          </div>
        )}

        {/* Stop List */}
        <div style={{
          backgroundColor: 'var(--surface-2)',
          borderRadius: '5px',
          padding: '1rem',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          <h3 style={{ color: 'var(--accent-primary)', margin: '0 0 0.75rem 0', fontSize: '1rem' }}>
            Route Stops
          </h3>
          {stops.map((stop, index) => (
            <div
              key={stop.id || index}
              onClick={() => setSelectedStop(index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '0.75rem',
                backgroundColor: selectedStop === index ? 'rgba(35, 134, 54, 0.1)' : 'transparent',
                borderRadius: '5px',
                cursor: 'pointer',
                marginBottom: index < stops.length - 1 ? '0.5rem' : 0,
                borderLeft: `3px solid ${getStopColor(stop.stop_type)}`
              }}
            >
              <div style={{
                width: '28px',
                height: '28px',
                backgroundColor: getStopColor(stop.stop_type),
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-primary)',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                flexShrink: 0
              }}>
                {index + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                  {stop.address}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  {stop.stop_type.charAt(0).toUpperCase() + stop.stop_type.slice(1)} #{stop.stop_sequence}
                  {stop.time_window_start && ` | ${fmtWindowTime(stop.time_window_start)}`}
                </div>
                {liveEtas[stop.id] && (
                  <div style={{ fontSize: '0.8rem', marginTop: '0.15rem' }}>
                    <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>Live ETA ~{liveEtas[stop.id].etaFormatted}</span>
                    {liveEtas[stop.id].deltaMinutes !== null && liveEtas[stop.id].deltaMinutes !== undefined && (
                      <span style={{ marginLeft: '0.4rem', color: liveEtas[stop.id].deltaMinutes > 20 ? 'var(--color-danger)' : liveEtas[stop.id].deltaMinutes > 5 ? 'var(--color-warning)' : 'var(--color-success)' }}>
                        {liveEtas[stop.id].deltaMinutes > 0 ? `${liveEtas[stop.id].deltaMinutes}m late` : liveEtas[stop.id].deltaMinutes < 0 ? `${-liveEtas[stop.id].deltaMinutes}m early` : 'on time'}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {index < stops.length - 1 && route?.legs?.[index] && (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', textAlign: 'right' }}>
                  <div>{route.legs[index].distance?.text}</div>
                  <div>{route.legs[index].duration?.text}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem',
          marginTop: '1rem'
        }}>
          {liveEtaMsg && <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', alignSelf: 'center', marginRight: 'auto' }}>{liveEtaMsg}</span>}
          <button onClick={handleLiveEtas} disabled={liveEtaLoading}
            style={{ padding: '0.75rem 1.5rem', backgroundColor: 'var(--color-info)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: liveEtaLoading ? 'not-allowed' : 'pointer', fontWeight: 'bold', opacity: liveEtaLoading ? 0.6 : 1 }}>
            {liveEtaLoading ? 'Calculating…' : 'Live ETAs from GPS'}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'var(--accent-primary)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default RouteMapModal;
